import React from 'react'
import { List, useTable, ShowButton, EditButton, DateField } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space, Tabs } from 'antd'
import { brand } from '../../theme/brand'
import { useRowShow, ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { renderAvatarName } from '../../theme/columns'
import { Sparkles, Clock, BadgeCheck, XCircle } from '../../components/icons'
import { STATUS_OPTIONS, statusLabel, statusTone, type SuggestionStatus } from './suggestionStatus'
import { SuggestionBoxSwitch } from './SuggestionBoxSwitch'

export interface Suggestion {
  id: string
  user_id: string
  body: string
  status: SuggestionStatus
  created_at: string
  // FK embed (suggestions_user_profile_fkey → profiles.user_id). `author.id` is
  // profiles.id — the ONLY id /profiles/show/:id resolves. user_id is a different
  // uuid in every row, so a profile link built from it opens nobody.
  author?: { id: string; name: string | null; class_year: string | null; avatar_url: string | null } | null
}

/** Author profile pulled inline via the named profiles FK (no second round trip). */
export const SELECT = '*, author:profiles!suggestions_user_profile_fkey(id,name,class_year,avatar_url)'

/**
 * Suggestion Box — the admin triage queue for member suggestions.
 *
 * Read-only here: triage (status + the private admin note) happens on the Edit
 * page, and there is deliberately NO Delete anywhere — the table has no admin
 * DELETE policy, so a Delete button would be a silent no-op (the rsvps lesson).
 *
 * The status Tabs set/clear a single `status eq` filter through useTable's own
 * `setFilters` (the businesses pattern), so the active tab and
 * `syncWithLocation`'s querystring never drift apart. Each tab carries its
 * count: five bounded pageSize:1 queries reading `result.total`, spelled out
 * (not looped) so the hook call order is statically obvious — PostgREST returns
 * the counts without ever shipping the rows.
 *
 * SuggestionBoxSwitch sits above the queue: the real-time on/off switch for
 * the whole feature (feature_flags.suggestion_box_enabled), where the admins
 * who read the ideas can also close the box.
 */
export function SuggestionList() {
  const { tableProps, filters, setFilters, setCurrentPage } = useTable<Suggestion>({
    resource: 'suggestions',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    meta: { select: SELECT },
  })
  const onRow = useRowShow('suggestions')

  const activeStatusFilter = filters.find(
    (f) => 'field' in f && f.field === 'status' && f.operator === 'eq',
  )
  const activeTab =
    activeStatusFilter && 'value' in activeStatusFilter ? String(activeStatusFilter.value) : 'all'

  // Refine's setFilters never resets the page on its own — without this, an
  // admin on page 2 of "All" who clicks "New" lands on offset 10 of a filtered
  // set that may have far fewer rows, and sees an empty table even though the
  // tab shows a nonzero count.
  function handleTabChange(key: string) {
    setFilters(key === 'all' ? [] : [{ field: 'status', operator: 'eq', value: key }], 'replace')
    setCurrentPage(1)
  }

  const newQ = useList<Suggestion>({
    resource: 'suggestions',
    filters: [{ field: 'status', operator: 'eq', value: 'new' }],
    pagination: { pageSize: 1 },
  })
  const reviewingQ = useList<Suggestion>({
    resource: 'suggestions',
    filters: [{ field: 'status', operator: 'eq', value: 'reviewing' }],
    pagination: { pageSize: 1 },
  })
  const plannedQ = useList<Suggestion>({
    resource: 'suggestions',
    filters: [{ field: 'status', operator: 'eq', value: 'planned' }],
    pagination: { pageSize: 1 },
  })
  const shippedQ = useList<Suggestion>({
    resource: 'suggestions',
    filters: [{ field: 'status', operator: 'eq', value: 'shipped' }],
    pagination: { pageSize: 1 },
  })
  const declinedQ = useList<Suggestion>({
    resource: 'suggestions',
    filters: [{ field: 'status', operator: 'eq', value: 'declined' }],
    pagination: { pageSize: 1 },
  })

  const counts: Record<SuggestionStatus, number> = {
    new: newQ.result?.total ?? 0,
    reviewing: reviewingQ.result?.total ?? 0,
    planned: plannedQ.result?.total ?? 0,
    shipped: shippedQ.result?.total ?? 0,
    declined: declinedQ.result?.total ?? 0,
  }
  const inProgress = counts.reviewing + counts.planned

  const stats: Stat[] = [
    { label: 'Awaiting triage', value: counts.new.toLocaleString(), icon: Clock, tone: counts.new > 0 ? 'gold' : 'neutral' },
    { label: 'In progress', value: inProgress.toLocaleString(), icon: Sparkles, tone: inProgress > 0 ? 'jaguar' : 'neutral' },
    { label: 'Shipped', value: counts.shipped.toLocaleString(), icon: BadgeCheck, tone: counts.shipped > 0 ? 'green' : 'neutral' },
    { label: 'Declined', value: counts.declined.toLocaleString(), icon: XCircle, tone: 'neutral' },
  ]

  const tabItems = [
    { key: 'all', label: 'All' },
    ...STATUS_OPTIONS.map((o) => ({
      key: o.value,
      label: (
        <Space size={6}>
          <span>{o.label}</span>
          <Pill tone={counts[o.value] > 0 ? statusTone(o.value) : 'neutral'}>{counts[o.value].toLocaleString()}</Pill>
        </Space>
      ),
    })),
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Suggestion Box',
        description:
          'Ideas members send from the Suggestion Box in the app, newest first. Open one to read it in full, then set where it stands and keep a private note — members never see the note.',
      }}
      stats={stats}
      statColumns={4}
    >
      <SuggestionBoxSwitch />
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} style={{ marginBottom: 12 }} />
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Suggestion>
              dataIndex="author"
              title="Member"
              render={(_: unknown, record) => (
                <Space direction="vertical" size={2}>
                  {renderAvatarName(record.author?.name ?? 'Unknown member')}
                  {record.author?.class_year ? (
                    <span style={{ fontFamily: brand.fSans, fontSize: 11, color: brand.sub, marginLeft: 40 }}>
                      Class of {record.author.class_year}
                    </span>
                  ) : null}
                </Space>
              )}
            />
            <Table.Column<Suggestion>
              dataIndex="body"
              title="Suggestion"
              render={(body: string) => (
                <span
                  data-testid="suggestion-body"
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 500,
                    fontSize: 12.5,
                    color: brand.ink,
                    display: 'inline-block',
                    maxWidth: 360,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={body}
                >
                  {body}
                </span>
              )}
            />
            <Table.Column<Suggestion>
              dataIndex="status"
              title="Status"
              render={(status: string) => <Pill tone={statusTone(status)}>{statusLabel(status)}</Pill>}
            />
            <Table.Column<Suggestion>
              dataIndex="created_at"
              title="Sent"
              sorter
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Suggestion>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
